import { useState, useEffect, useRef } from 'react';
import iconMenu from '@/assets/icons/menu.svg';
import iconCross from '@/assets/icons/icon-cross.svg';
import type { MenuItem } from '@/types/app';
import { pickNavIcon } from './navIcons';

interface NavProps {
  primary: MenuItem[];
  secondary: MenuItem[];
}

function getItemPath(url: string): string {
  if (!url) return '';
  try {
    const base = typeof window !== 'undefined' ? window.location.origin : 'http://_';
    return new URL(url, base).pathname;
  } catch {
    return url;
  }
}

function renderItem(item: MenuItem, currentPath: string, showIcon = true) {
  const Icon = showIcon ? pickNavIcon(item.classes) : null;
  const itemPath = getItemPath(item.url);
  const isActive = itemPath ? currentPath.startsWith(itemPath) : false;
  return (
    <li key={item.id} className="nav__item">
      <a
        href={item.url}
        target={item.target || undefined}
        className={`nav__link${isActive ? ' nav__link--active' : ''}`}
      >
        {Icon && <Icon className="nav__icon" />}
        <span>{item.title}</span>
      </a>
    </li>
  );
}

export function Nav({ primary, secondary }: NavProps) {
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [currentPath, setCurrentPath] = useState('');
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Deliberate post-hydration switch: window is unavailable during SSR, so the active link can only
    // be resolved after mount.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCurrentPath(window.location.pathname);
  }, []);

  useEffect(() => {
    if (isMobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobileOpen]);

  useEffect(() => {
    if (!isDropdownOpen) return;
    const handleClickOutside = (event: MouseEvent) => {
      if (!dropdownRef.current?.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isDropdownOpen]);

  return (
    <nav className={`nav${isMobileOpen ? ' nav--open' : ''}`}>
      <button
        className="nav__burger"
        type="button"
        aria-label={isMobileOpen ? 'Fermer le menu' : 'Ouvrir le menu'}
        aria-expanded={isMobileOpen}
        aria-controls="nav-panel"
        onClick={() => setIsMobileOpen(!isMobileOpen)}
      >
        <img src={isMobileOpen ? iconCross : iconMenu} alt="" width={24} height={24} />
      </button>

      {/* Desktop : items primaires en row */}
      <ul className="nav__list nav__list--primary">
        {primary.map((item) => renderItem(item, currentPath))}
      </ul>

      {/* Desktop : bouton Menu + dropdown des items secondaires */}
      {secondary.length > 0 && (
        <div className="nav__dropdown-wrapper" ref={dropdownRef}>
          <button
            type="button"
            className="nav__menu-toggle"
            aria-expanded={isDropdownOpen}
            aria-haspopup="true"
            onClick={() => setIsDropdownOpen((open) => !open)}
          >
            <img src={iconMenu} alt="" className="nav__menu-toggle-icon" width={18} height={18} />
            <span>Menu</span>
          </button>
          {isDropdownOpen && (
            <div className="nav__dropdown nav__dropdown--open">
              <ul className="nav__dropdown-list">
                {secondary.map((item) => renderItem(item, currentPath))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* Mobile : panel ouvert par le burger */}
      <div className="nav__panel" id="nav-panel" inert={!isMobileOpen}>
        <ul className="nav__list nav__list--mobile">
          {primary.map((item) => renderItem(item, currentPath, true))}
        </ul>

        {secondary.length > 0 && (
          <ul className="nav__list nav__list--mobile-secondary">
            {secondary.map((item) => renderItem(item, currentPath, false))}
          </ul>
        )}
      </div>
    </nav>
  );
}
